/* =====================================
	 3. CHART FOOTNOTE
	 ===================================== */
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { resolveMarkerSymbolsInText } from "./StatMarkerUtils";
import { ChartRenderedFromType } from "src/pages/Insights/models/interfaces";
import { EllipsisText } from "./EllipsisText";

interface ILowNItem {
	code: string;
	label?: string;
}

interface ILowNMessage {
	items?: ILowNItem[];
}

interface IFootnoteObj {
	low_n_message?: ILowNMessage;
	q_txt?: string;
	src?: string;
	other?: string;
	stat_message?: string;
	stat_test_performed?: boolean;
}
import { RootState } from "src/store";

interface IChartFootnoteProps {
	src?: string;
	analysis_name?: string;
	hasLowNSize?: boolean;
	footnoteObj?: IFootnoteObj;
	isWeighted?: boolean;
	stat_message?: string;
	className?: string;
	valueParams?: string[];
	renderedFrom?: ChartRenderedFromType;
}

interface ICIGroup {
	ciLevel: string;
	entries: { code: string; label: string }[];
	/** Original raw text inside brackets, used for replacement */
	rawSegment: string;
}

interface IParsedStatMarkers {
	prefix: string;
	groups: ICIGroup[];
	isMultiCI: boolean;
	/** Full marker part after "| Markers- " — preserved for multi-CI rendering */
	markerRaw: string;
}

const parseStatMessageMarkers = (statMessage: string): IParsedStatMarkers | null => {
	if (!statMessage) return null;

	const markerIdx = statMessage.indexOf("| Markers-");
	if (markerIdx === -1) return null;

	const prefix = statMessage.slice(0, markerIdx).trim();
	const markerPart = statMessage.slice(markerIdx + "| Markers-".length).trim();

	const groups: ICIGroup[] = [];

	// Multi-CI: supports both {95}% [...] and 0.95% [...] formats
	const multiCIRegex = /(?:\{(\d+)\}|(\d+(?:\.\d+)?))%\s*\[([^\]]+)\]/g;
	let match: RegExpExecArray | null;
	while ((match = multiCIRegex.exec(markerPart)) !== null) {
		groups.push({
			ciLevel: match[1] ?? match[2],
			entries: parseMarkerEntries(match[3]),
			rawSegment: match[3],
		});
	}

	if (groups.length > 1) {
		return { prefix, groups, isMultiCI: true, markerRaw: markerPart };
	}

	groups.length = 0;
	const entries = parseMarkerEntries(markerPart);
	if (entries.length) {
		groups.push({ ciLevel: "", entries, rawSegment: markerPart });
	}

	return groups.length > 0 ? { prefix, groups, isMultiCI: false, markerRaw: markerPart } : null;
};

const parseMarkerEntries = (raw: string): { code: string; label: string }[] => {
	const entries: { code: string; label: string }[] = [];
	for (const entry of raw.split(",")) {
		const colonIdx = entry.indexOf(":");
		if (colonIdx === -1) continue;
		const code = entry.slice(0, colonIdx).trim();
		const label = entry.slice(colonIdx + 1).trim();
		if (code && label) entries.push({ code, label });
	}
	return entries;
};

// ─── AskKural grid cell ───────────────────────────────────────────────────────

interface IGridCellProps {
	label: string;
	value: React.ReactNode;
	/** Show a left border divider (all cells except the first in a row) */
	divider?: boolean;
}

const FootnoteGridCell: React.FC<IGridCellProps> = ({ label, value, divider }) => (
	<div className={`flex flex-col gap-0.5 px-3 ${divider ? "border-r border-[--border-color]" : ""}`}>
		<span className="text-base-11 leading-[1.3] text-[--neutral-grey]">{label}</span>
		<span className="text-base-11 leading-[1.4] text-[--secondary-color]">{value}</span>
	</div>
);

// ─── AskKural footnote layout ─────────────────────────────────────────────────

interface IAskKuralFootnoteProps {
	src?: string;
	analysis_name?: string;
	hasLowNSize?: boolean;
	footnoteObj?: IFootnoteObj;
	isWeighted?: boolean;
	stat_message?: string;
	valueParams?: string[];
	studyName?: string;
	resolvedStatMessage?: string;
	parsedStatMarkers: IParsedStatMarkers | null;
	quesNo?: string;
	questionText?: string;
	weightLabel: string | null;
}

/**
 * Parses a stat_message prefix into { dataType, statTest }.
 *
 * Two formats are supported:
 *
 * 1. Explicit pipe separator (Chi-Square payloads):
 *    "Categorical, Ordinal | Chi-Square, Confidence Interval: 95%"
 *    → dataType: "Categorical, Ordinal"
 *    → statTest: "Chi-Square, Confidence Interval: 95%"
 *
 * 2. ANOVA / single-phrase prefix (no pipe before "| Markers-"):
 *    "ANOVA applied across field_id at 95% confidence interval"
 *    → dataType: undefined
 *    → statTest: "ANOVA applied across field_id at 95% confidence interval"
 */
const parseStatPrefix = (prefix: string): { dataType?: string; statTest?: string } => {
	const trimmed = prefix.trim();
	if (!trimmed) return {};

	const pipeIdx = trimmed.indexOf("|");
	if (pipeIdx === -1) {
		// Single phrase — treat entire prefix as Stat Test, no Data Type
		return { statTest: trimmed };
	}
	return {
		dataType: trimmed.slice(0, pipeIdx).trim() || undefined,
		statTest: trimmed.slice(pipeIdx + 1).trim() || undefined,
	};
};

const AskKuralFootnote: React.FC<IAskKuralFootnoteProps> = ({
	src,
	analysis_name,
	hasLowNSize,
	footnoteObj,
	isWeighted,
	valueParams,
	studyName,
	resolvedStatMessage,
	parsedStatMarkers,
	quesNo,
	questionText,
	weightLabel,
}) => {
	// ── Derive grid cell values ───────────────────────────────────────────────

	// Data Type + Stat Test come from parsedStatMarkers.prefix or resolvedStatMessage
	const { dataType, statTest } = useMemo(() => {
		if (parsedStatMarkers?.prefix) return parseStatPrefix(parsedStatMarkers.prefix);
		if (resolvedStatMessage) {
			// If no marker section, treat the whole string as statTest
			return parseStatPrefix(resolvedStatMessage);
		}
		return {};
	}, [parsedStatMarkers, resolvedStatMessage]);

	// Filter: flat marker entries from the first (or only) CI group
	const filterEntries = useMemo(() => {
		if (!parsedStatMarkers) return [];
		// For single-CI just take the first group's entries.
		// For multi-CI flatten all groups.
		return parsedStatMarkers.groups.flatMap((g) => g.entries);
	}, [parsedStatMarkers]);

	const filterLabel = useMemo(() => {
		if (!filterEntries.length) return undefined;
		return filterEntries.map((e) => `${e.code}: ${e.label}`).join(", ");
	}, [filterEntries]);

	// Low n-size label
	const lowNLabel = useMemo(() => {
		if (!hasLowNSize) return undefined;
		const items = footnoteObj?.low_n_message?.items;
		if (items?.length) return items.map((i) => i.label ?? i.code).join(", ");
		return "If available";
	}, [hasLowNSize, footnoteObj]);

	// Weights
	const weightsLabel = useMemo(() => {
		if (weightLabel) return weightLabel;
		return undefined;
	}, [weightLabel]);

	// Analysis type: analysis_name, or valueParams bracket list
	const analysisLabel = useMemo(() => {
		if (analysis_name) return analysis_name;
		if (valueParams?.length) return `[${valueParams.join(", ")}]`;
		return undefined;
	}, [analysis_name, valueParams]);

	// ── Build rows (first row: up to 4 cells; second row: up to 4 cells) ─────

	// All footnote grid cells in a single flat list — flex-wrap handles row breaks
	const gridCells = useMemo(() => {
		const cells: { label: string; value: React.ReactNode }[] = [];
		if (dataType) cells.push({ label: "Data Type", value: dataType });
		if (statTest) cells.push({ label: "Stat Test", value: statTest });
		if (weightsLabel) cells.push({ label: "Weights", value: weightsLabel });
		if (lowNLabel) cells.push({ label: "Low- n-size", value: lowNLabel });
		if (analysisLabel) cells.push({ label: "Analysis Type", value: analysisLabel });
		if (filterLabel) cells.push({ label: "Stat Marker", value: filterLabel });
		return cells;
	}, [dataType, statTest, weightsLabel, lowNLabel, analysisLabel, filterLabel]);

	const hasGrid = gridCells.length > 0;
	const hasQuestion = Boolean(quesNo || questionText);

	if (!hasGrid && !hasQuestion && !studyName) return null;

	return (
		<div className="flex flex-col gap-1 pb-2 mt-1 text-[11px] font-sans text-[--secondary-color] text-left">
			{/* Question line */}
			{hasQuestion && (
				<div className="px-3 leading-[1.4]">
					{quesNo && <span className="font-semibold text-[--primary-color]">{quesNo}. </span>}
					{questionText && <EllipsisText text={questionText} maxLines={3} lineHeight={20} />}
				</div>
			)}

			{/* Grid rows */}
			{/* Grid cells — single flex-wrap row, naturally breaks to next line */}
			{hasGrid && (
				<div className="flex flex-row flex-wrap">
					{gridCells.map((cell, idx) => (
						<FootnoteGridCell key={cell.label} label={cell.label} value={cell.value} divider={idx < gridCells.length - 1} />
					))}
				</div>
			)}

			{/* Study name */}
			{studyName && (
				<div className="px-3">
					<EllipsisText text={studyName} maxLines={1} lineHeight={20} className="text-[--primary-color] underline cursor-pointer" />
				</div>
			)}
		</div>
	);
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ChartFootnote: React.FC<IChartFootnoteProps> = ({
	src,
	analysis_name,
	hasLowNSize = false,
	footnoteObj = {},
	isWeighted,
	stat_message,
	className = "",
	valueParams = [],
	renderedFrom,
}) => {
	const { selectedStudy } = useSelector((state: RootState) => state.studiesSlice);
	const low_n_message = footnoteObj?.low_n_message;
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);

	const { quesNo, questionText } = useMemo(() => {
		if (!src?.trim()) return { quesNo: undefined, questionText: undefined };
		const match = src.trim().match(/^([a-zA-Z0-9_]+)\.\s*([\s\S]*)$/);
		if (match) {
			return { quesNo: match[1]?.trim(), questionText: match[2]?.trim() };
		}
		return { quesNo: undefined, questionText: src.trim() };
	}, [src]);

	const weightLabel = useMemo(() => {
		if (isWeighted === true) return "Weighted data";
		if (isWeighted === false) return "Unweighted data";
		return null;
	}, [isWeighted]);

	// Prefer the explicit prop; fall back to footnoteObj.stat_message
	const effectiveStatMessage = stat_message ?? footnoteObj?.stat_message;
	const resolvedStatMessage = useMemo(() => {
		return effectiveStatMessage ? resolveMarkerSymbolsInText(effectiveStatMessage) : effectiveStatMessage;
	}, [effectiveStatMessage]);

	// Derive hasLowNSize from footnoteObj if the prop is not explicitly set
	const effectiveHasLowNSize = hasLowNSize || !!footnoteObj?.low_n_message?.items?.length;

	const parsedStatMarkers = useMemo(() => parseStatMessageMarkers(resolvedStatMessage ?? ""), [resolvedStatMessage]);

	const hasFirstLine = Boolean(quesNo || questionText);

	const secondLineItems = useMemo(() => {
		const items: React.ReactNode[] = [];

		if (hasLowNSize) {
			items.push(
				<span key="low-n" className="text-[--nSizeLabel-color]">
					Low n-size
				</span>,
			);
		}
		if (analysis_name) {
			items.push(<span key="analysis">{analysis_name}</span>);
		}
		if (valueParams && valueParams.length > 0) {
			items.push(
				<span key="values">
					[
					{valueParams.map((val, idx) => (
						<React.Fragment key={val}>
							{idx > 0 && ", "}
							{val}
						</React.Fragment>
					))}
					]
				</span>,
			);
		}
		if (weightLabel) {
			items.push(<span key="weight">{weightLabel}</span>);
		}

		if (parsedStatMarkers) {
			if (parsedStatMarkers.isMultiCI) {
				const markerRaw = parsedStatMarkers.markerRaw;
				const segments: React.ReactNode[] = [];
				let lastIndex = 0;
				const bracketRegex = /\[([^\]]+)\]/g;
				let bMatch: RegExpExecArray | null;
				while ((bMatch = bracketRegex.exec(markerRaw)) !== null) {
					if (bMatch.index > lastIndex) segments.push(markerRaw.slice(lastIndex, bMatch.index));
					const innerEntries = parseMarkerEntries(bMatch[1]);
					segments.push(
						<React.Fragment key={`bracket-${bMatch.index}`}>
							{"["}
							{innerEntries.map((entry, eIdx) => (
								<React.Fragment key={`${bMatch!.index}-${entry.code}`}>
									{eIdx > 0 && ", "}
									<span className="text-[--nSizeLabel-color]">{entry.code}</span>
									{": "}
									{entry.label}
								</React.Fragment>
							))}
							{"]"}
						</React.Fragment>,
					);
					lastIndex = bMatch.index + bMatch[0].length;
				}
				if (lastIndex < markerRaw.length) segments.push(markerRaw.slice(lastIndex));
				items.push(
					<span key="stat">
						{parsedStatMarkers.prefix}
						{" | Markers- "}
						{segments}
					</span>,
				);
			} else {
				items.push(
					<span key="stat">
						{parsedStatMarkers.prefix}
						{" | Markers- "}
						{parsedStatMarkers.groups[0].entries.map((entry, eIdx) => (
							<React.Fragment key={entry.code}>
								{eIdx > 0 && ", "}
								<span className="text-[--nSizeLabel-color]">{entry.code}</span>
								{": "}
								{entry.label}
							</React.Fragment>
						))}
					</span>,
				);
			}
		} else if (resolvedStatMessage) {
			items.push(<span key="stat">{resolvedStatMessage}</span>);
		}

		if (selectedStudy?.study_name) {
			items.push(
				<span key="study" className="text-[#4CBB96]">
					{selectedStudy.study_name}
				</span>,
			);
		}

		return items;
	}, [low_n_message, hasLowNSize, analysis_name, weightLabel, parsedStatMarkers, resolvedStatMessage, selectedStudy?.study_name]);

	const hasSecondLine = secondLineItems.length > 0;
	if (!hasFirstLine && !hasSecondLine) return null;
	// ── AskKural inline grid layout ───────────────────────────────────────────
	if (renderedFrom === ChartRenderedFromType.AskKural) {
		return (
			<AskKuralFootnote
				src={src}
				analysis_name={analysis_name ?? footnoteObj?.notes}
				hasLowNSize={effectiveHasLowNSize}
				footnoteObj={footnoteObj}
				isWeighted={isWeighted}
				valueParams={valueParams}
				studyName={selectedStudy?.study_name}
				resolvedStatMessage={resolvedStatMessage}
				parsedStatMarkers={parsedStatMarkers}
				quesNo={quesNo}
				questionText={questionText}
				weightLabel={weightLabel}
			/>
		);
	}

	// ── Original inline layout (unchanged below) ──────────────────────────────

	if (renderedFrom === ChartRenderedFromType.AskKuralInline) return null;
	if (isMultiTileSlide) return null;
	const mergedContent = (
		<div>
			{hasFirstLine && (
				<div>
					{quesNo && <span className="font-semibold text-[#4CBB96]">{quesNo}. </span>}
					{questionText && <span>{questionText}</span>}
				</div>
			)}
			{hasSecondLine && (
				<div className="leading-[1.4]">
					{secondLineItems.map((node, idx) => (
						<React.Fragment key={idx}>
							{idx > 0 && <span> | </span>}
							{node}
						</React.Fragment>
					))}
				</div>
			)}
		</div>
	);

	return (
		<div className={`sticky bottom-0 leading-[1.4] py-1 mt-1 text-[11px] tracking-[0.025rem] font-sans text-[--secondary-color] px-3 text-left ${className}`}>
			<EllipsisText text={mergedContent} maxLines={6} lineHeight={16}>
				{mergedContent}
			</EllipsisText>
		</div>
	);
};
